import { ethers } from 'ethers';
import { poolAbi } from '../abis';
import { poolAddress } from '../constants';
import { HistoryItem } from '../types';
import { explorerService } from './explorerService';
import { NetworkConnectivityHelper } from '../utils/networkConnectivity';

export interface PoolDepositEvent {
  user: string;
  amount: string;
  blockNumber: number;
  transactionHash: string;
  timestamp: Date;
}

class PoolHistoryService {
  private provider: ethers.BrowserProvider | null = null;
  private contract: ethers.Contract | null = null;
  private eventListeners: Map<string, () => void> = new Map();

  constructor() {
    this.initializeProvider().catch(error => {
      console.error('Failed to initialize pool history service:', error);
    });
    this.setupNetworkChangeListener();
  }

  private async initializeProvider(): Promise<void> {
    if (typeof window !== 'undefined' && window.ethereum) {
      try {
        this.provider = new ethers.BrowserProvider(window.ethereum);
        this.contract = new ethers.Contract(
          poolAddress,
          poolAbi,
          this.provider
        );
        // Do not call getBlockNumber() here; RPC can be throttled on load.
        console.log('Pool history service provider/contract prepared');
      } catch (error) {
        console.error('Error initializing pool history service:', error);
        // Don't throw, just log the error to prevent app crash
        this.provider = null;
        this.contract = null;
      }
    }
  }

  private setupNetworkChangeListener(): void {
    if (typeof window !== 'undefined' && window.ethereum) {
      window.ethereum.on('chainChanged', () => {
        // Clean up existing event listeners first
        this.cleanupAllEventListeners();
        // Reinitialize provider and contract on network change
        this.initializeProvider().catch(error => {
          console.error(
            'Failed to reinitialize pool history service after network change:',
            error
          );
        });
      });
    }
  }

  private cleanupAllEventListeners(): void {
    this.eventListeners.forEach(cleanup => {
      try {
        cleanup();
      } catch (error) {
        console.error('Error cleaning up event listener:', error);
      }
    });
    this.eventListeners.clear();
  }

  /**
   * Fetch deposit events from Pool contract for a specific user
   */
  async fetchUserDepositEvents(
    userAddress: string,
    fromBlock?: number
  ): Promise<PoolDepositEvent[]> {
    if (!this.contract || !this.provider) {
      console.warn('Pool contract not initialized, skipping events fetch');
      return [];
    }

    // Check RPC connectivity first
    const hasRpcConnectivity =
      await NetworkConnectivityHelper.checkRpcConnectivity(this.provider);
    if (!hasRpcConnectivity) {
      console.warn(
        'RPC connectivity issues detected, skipping deposit events fetch'
      );
      return [];
    }

    return NetworkConnectivityHelper.retryWithBackoff(
      async () => {
        try {
          // Get safe block range for VPN/connectivity issues
          const blockRange = await NetworkConnectivityHelper.getSafeBlockRange(
            this.provider!,
            5000
          );
          if (!blockRange) {
            console.warn('Could not determine safe block range');
            return [];
          }

          const safeFromBlock = fromBlock ?? blockRange.fromBlock;

          // Deposit event has no indexed params; use wildcard filter and post-filter in code
          const filter = this.contract!.filters.Deposit();

          console.log(
            `Fetching deposit events from block ${safeFromBlock} to ${blockRange.toBlock}`
          );
          const events = await this.contract!.queryFilter(
            filter,
            safeFromBlock,
            blockRange.toBlock
          );

          let targetAddress: string | null = null;
          try {
            targetAddress = ethers.getAddress(userAddress);
          } catch {
            targetAddress = userAddress.toLowerCase();
          }
          const depositEvents: PoolDepositEvent[] = [];

          for (const event of events) {
            if ('args' in event && event.args) {
              const [userArg, amountArg] = event.args as unknown as [
                string,
                any,
              ];
              let eventUser: string | null = null;
              if (typeof userArg === 'string') {
                try {
                  eventUser = ethers.getAddress(userArg);
                } catch {
                  eventUser = userArg.toLowerCase();
                }
              }
              if (eventUser && targetAddress && eventUser === targetAddress) {
                // Get block timestamp
                const block = await this.provider!.getBlock(event.blockNumber);
                const timestamp = block
                  ? new Date(block.timestamp * 1000)
                  : new Date();

                depositEvents.push({
                  user: userArg,
                  amount: amountArg.toString(),
                  blockNumber: event.blockNumber,
                  transactionHash: event.transactionHash,
                  timestamp,
                });
              }
            }
          }

          // Sort by timestamp (newest first)
          return depositEvents.sort(
            (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
          );
        } catch (error: any) {
          console.error('Error fetching deposit events:', error);

          // If it's a block range error, try with an even smaller range
          if (
            error.message?.includes('invalid block range') ||
            error.code === -32603
          ) {
            console.log(
              'Block range error detected, trying with minimal range...'
            );

            const blockRange =
              await NetworkConnectivityHelper.getSafeBlockRange(
                this.provider!,
                500
              );
            if (!blockRange) {
              return [];
            }

            const filter = this.contract!.filters.Deposit();
            const events = await this.contract!.queryFilter(
              filter,
              blockRange.fromBlock,
              blockRange.toBlock
            );

            let targetAddress: string | null = null;
            try {
              targetAddress = ethers.getAddress(userAddress);
            } catch {
              targetAddress = userAddress.toLowerCase();
            }
            const depositEvents: PoolDepositEvent[] = [];
            for (const event of events) {
              if ('args' in event && event.args) {
                const [userArg, amountArg] = event.args as unknown as [
                  string,
                  any,
                ];
                let eventUser: string | null = null;
                if (typeof userArg === 'string') {
                  try {
                    eventUser = ethers.getAddress(userArg);
                  } catch {
                    eventUser = userArg.toLowerCase();
                  }
                }
                if (eventUser && targetAddress && eventUser === targetAddress) {
                  const block = await this.provider!.getBlock(
                    event.blockNumber
                  );
                  const timestamp = block
                    ? new Date(block.timestamp * 1000)
                    : new Date();

                  depositEvents.push({
                    user: userArg,
                    amount: amountArg.toString(),
                    blockNumber: event.blockNumber,
                    transactionHash: event.transactionHash,
                    timestamp,
                  });
                }
              }
            }

            return depositEvents.sort(
              (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
            );
          }

          // For other errors, throw to trigger retry
          throw error;
        }
      },
      2,
      2000
    ).catch(error => {
      console.error('All retry attempts failed:', error);
      return []; // Return empty array to prevent app crash
    });
  }

  /**
   * Convert Pool deposit event to HistoryItem format
   */
  convertToHistoryItem(event: PoolDepositEvent): HistoryItem {
    // Format amount from wei (9 decimals for SWM)
    const formattedAmount = ethers.formatUnits(event.amount, 9);

    return {
      id: `pool-deposit-${event.transactionHash}`,
      type: 'deposit',
      amount: formattedAmount,
      currency: 'SWM',
      timestamp: event.timestamp,
      status: 'completed', // Pool events are always completed
      description: `SWM deposit to Pool contract`,
      transactionHash: event.transactionHash,
    };
  }

  /**
   * Get explorer URL for transaction hash
   */
  getExplorerUrl(transactionHash: string): string {
    return explorerService.getTransactionUrl(transactionHash);
  }

  /**
   * Listen for new deposit events in real-time
   */
  listenForDepositEvents(
    userAddress: string,
    callback: (event: PoolDepositEvent) => void
  ): () => void {
    if (!this.contract) {
      console.warn(
        'Pool contract not initialized, skipping event listener setup'
      );
      return () => {}; // Return empty cleanup function
    }

    const listenerId = `${userAddress}-${Date.now()}`;

    try {
      // Deposit event has no indexed params; use wildcard filter and filter inside listener
      const filter = this.contract.filters.Deposit();

      const eventListener = async (
        user: string,
        amount: bigint,
        event: any
      ) => {
        try {
          if (!this.provider) return;
          if (
            typeof user !== 'string' ||
            user.toLowerCase() !== userAddress.toLowerCase()
          ) {
            return;
          }

          const block = await this.provider.getBlock(event.blockNumber);
          const timestamp = block
            ? new Date(block.timestamp * 1000)
            : new Date();

          const depositEvent: PoolDepositEvent = {
            user,
            amount: amount.toString(),
            blockNumber: event.blockNumber,
            transactionHash: event.transactionHash,
            timestamp,
          };

          callback(depositEvent);
        } catch (error) {
          console.error('Error processing deposit event:', error);
        }
      };

      this.contract.on(filter, eventListener);

      // Store cleanup function
      const cleanup = () => {
        try {
          if (this.contract) {
            this.contract.off(filter, eventListener);
          }
        } catch (error) {
          console.error('Error cleaning up event listener:', error);
        }
        this.eventListeners.delete(listenerId);
      };

      this.eventListeners.set(listenerId, cleanup);

      // Return cleanup function
      return cleanup;
    } catch (error) {
      console.error('Error setting up deposit event listener:', error);
      // Return empty cleanup function if setup fails
      return () => {};
    }
  }

  /**
   * Check if the service is ready to use
   */
  isReady(): boolean {
    return this.provider !== null && this.contract !== null;
  }

  /**
   * Clean up all event listeners (useful for component unmounting)
   */
  cleanup(): void {
    this.cleanupAllEventListeners();
  }
}

export const poolHistoryService = new PoolHistoryService();
